import React, { useEffect, useState } from 'react';
import { Gender } from '../../utils/apiClient';
import { parseApiError } from '../../utils/apiError';
import {
  MergePerson,
  MergePreview,
  MergeResult,
  formatBirthDate,
  getMergePreview,
  mergePersons,
  personFullName,
} from '../../utils/personMergeApi';

interface MergeModalProps {
  initialPrimaryId: number;
  initialSecondaryId: number;
  onClose: () => void;
  onMerged: (result: MergeResult) => void;
}

const genderLabel = (gender?: Gender) =>
  gender === Gender.Male ? 'мужской' : gender === Gender.Female ? 'женский' : 'не указан';

const PersonBox: React.FC<{ person: MergePerson; title: string; accent?: boolean }> = ({ person, title, accent }) => (
  <div className={`merge-box${accent ? ' merge-box--primary' : ''}`}>
    <div className="merge-box__title">{title}</div>
    <div className="merge-box__name">{personFullName(person)}</div>
    <dl className="merge-box__list">
      <dt>Дата рождения</dt><dd>{formatBirthDate(person.birthDate)}</dd>
      <dt>Город</dt><dd>{person.city || '—'}</dd>
      <dt>Пол</dt><dd>{genderLabel(person.gender)}</dd>
      <dt>Вход (email)</dt>
      <dd>{person.hasAccount ? person.email : <span className="merge-box__muted">нет — анкета без входа</span>}</dd>
      <dt>Роли</dt><dd>{person.roles.length ? person.roles.join(', ') : '—'}</dd>
      <dt>Создана</dt><dd>{formatBirthDate(person.createdOn)}</dd>
    </dl>
    {person.isChild && <div className="merge-box__tag">Анкета ребёнка (заведена родителем)</div>}
    <div className="merge-box__apps-title">Заявки на турниры ({person.applications.length})</div>
    {person.applications.length === 0 ? (
      <div className="merge-box__muted">нет</div>
    ) : (
      <ul className="merge-box__apps">
        {person.applications.map((a) => (
          <li key={a.participantId}>
            {a.tournamentName}
            <span className="merge-box__muted"> · {a.weight} кг{a.organization ? ` · ${a.organization}` : ''}{a.hasMatches ? ' · есть поединки' : ''}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const MergeModal: React.FC<MergeModalProps> = ({ initialPrimaryId, initialSecondaryId, onClose, onMerged }) => {
  const [primaryId, setPrimaryId] = useState(initialPrimaryId);
  const [secondaryId, setSecondaryId] = useState(initialSecondaryId);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [keepOnConflict, setKeepOnConflict] = useState<'primary' | 'secondary' | ''>('');
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPreview(null);
    setKeepOnConflict('');
    getMergePreview(primaryId, secondaryId)
      .then((data) => { if (!cancelled) setPreview(data); })
      .catch((ex) => { if (!cancelled) setError(parseApiError(ex).message || 'Не удалось загрузить данные для объединения.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [primaryId, secondaryId]);

  const swap = () => {
    setPrimaryId(secondaryId);
    setSecondaryId(primaryId);
  };

  const hasConflicts = (preview?.conflicts.length ?? 0) > 0;
  const blocked = (preview?.blockers.length ?? 0) > 0;
  const canMerge = !!preview && !blocked && !merging && (!hasConflicts || keepOnConflict !== '');

  const handleMerge = async () => {
    if (!preview || !canMerge) return;
    const question = `Объединить анкеты?\n\nОсновная: ${personFullName(preview.primary)}\nУдалится: ${personFullName(preview.secondary)} (id ${preview.secondary.id})\n\nОтменить это действие нельзя.`;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(question)) return;

    setMerging(true);
    setError('');
    try {
      const result = await mergePersons(primaryId, secondaryId, keepOnConflict || undefined);
      onMerged(result);
    } catch (ex) {
      setError(parseApiError(ex).message || 'Не удалось объединить анкеты.');
      setMerging(false);
    }
  };

  return (
    <div className="person-duplicates__overlay" onClick={onClose}>
      <div className="person-duplicates__modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="person-duplicates__close" onClick={onClose} aria-label="Закрыть">×</button>
        <h2 className="person-duplicates__modal-title">Объединение анкет</h2>

        {loading && <div className="person-duplicates__state">Загрузка…</div>}
        {error && <div className="person-duplicates__alert" role="alert">{error}</div>}

        {preview && (
          <>
            <div className="merge-compare">
              <PersonBox person={preview.primary} title="Основная — останется" accent />
              <div className="merge-compare__swap">
                <button type="button" className="btn btn--back" onClick={swap} title="Поменять местами">⇄</button>
              </div>
              <PersonBox person={preview.secondary} title="Будет удалена" />
            </div>

            {hasConflicts && (
              <div className="merge-conflicts">
                <h3>Оба заявлены на один турнир — чью заявку оставить?</h3>
                {preview.conflicts.map((c) => (
                  <div key={c.tournamentId} className="merge-conflicts__item">
                    <strong>{c.tournamentName}</strong>
                    <span className="merge-box__muted">
                      {' '}— основная: {c.primary.weight} кг{c.primary.hasMatches ? ' (есть поединки)' : ''};
                      удаляемая: {c.secondary.weight} кг{c.secondary.hasMatches ? ' (есть поединки)' : ''}
                    </span>
                  </div>
                ))}
                <label className="merge-conflicts__option">
                  <input
                    type="radio"
                    name="keep-on-conflict"
                    checked={keepOnConflict === 'primary'}
                    onChange={() => setKeepOnConflict('primary')}
                  />
                  Оставить заявки основной анкеты
                </label>
                <label className="merge-conflicts__option">
                  <input
                    type="radio"
                    name="keep-on-conflict"
                    checked={keepOnConflict === 'secondary'}
                    onChange={() => setKeepOnConflict('secondary')}
                  />
                  Оставить заявки удаляемой анкеты (перейдут в основную)
                </label>
              </div>
            )}

            {preview.blockers.length > 0 && (
              <ul className="merge-blockers">
                {preview.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            )}

            <ul className="merge-notes">
              {preview.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>

            <div className="merge-actions">
              <button type="button" className="btn btn--back" onClick={onClose}>Отмена</button>
              <button type="button" className="btn btn--delete" onClick={handleMerge} disabled={!canMerge}>
                {merging ? 'Объединяем…' : 'Объединить'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MergeModal;
